import { FormInstance } from 'antd';
import styled from 'styled-components';
import { FormValues } from '../ChannelForm';
import { InputSearch, Text, Colors } from 'basic-ui-lib';
import { TypeMedia } from 'modules/channel/services/constants';
import { IContentItem } from 'modules/channel/interfaces/api';
import useGetManageLibrary from 'modules/media/services/hooks/useGetManageLibrary';
import { IMediaItem, MediaType } from 'modules/media/interfaces/api';
import IcFolder from 'modules/media/assets/icons/IcFolder';
import { useGetMedia } from 'modules/channel/services/hooks/useGetMedia';
import { Breadcrumb } from '../Breadcrumb';
import { useEffect, useState } from 'react';

interface MediaContentProps {
  form?: FormInstance<FormValues>;
}

export const upsertContentByZone = (items: IContentItem[] = [], next: IContentItem): IContentItem[] => {
  return [...items.filter((item) => item.zone !== next.zone), next];
};

const MediaContent = ({ form }: MediaContentProps) => {
  const [data, mutate] = useGetManageLibrary();
  const { items, pathStack, openFolder, onBack } = useGetMedia(data, mutate);
  const [searchValue, setSearchValue] = useState('');
  const [filteredItems, setFilteredItems] = useState<IMediaItem[]>(items);

  const onPickMedia = (item: IMediaItem) => {
    const activeZone = form?.getFieldValue('activeZone');
    if (!activeZone) return;

    const oldItems = form?.getFieldValue('contentItems') || [];

    const nextItem: IContentItem = {
      type: item.type === MediaType.IMAGE ? TypeMedia.IMAGE : TypeMedia.VIDEO,
      url: item.url || '',
      zone: activeZone,
    };

    form?.setFieldsValue({
      contentItems: upsertContentByZone(oldItems, nextItem),
    });
  };

  const onClickMedia = (item: IMediaItem) => {
    if (item.type === MediaType.FOLDER) {
      openFolder(item);
    } else {
      onPickMedia(item);
    }
  };

  useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredItems(items);
    } else {
      const lowerSearch = searchValue.toLowerCase();
      setFilteredItems(items.filter((item) => item.name.toLowerCase().includes(lowerSearch)));
    }
  }, [searchValue, items]);
      
  return (
    <Container>
      <InputSearch placeholder='Search image/video' onChange={(value) => setSearchValue(value)} />
      <Breadcrumb path={pathStack} onBack={onBack} items={items} />
      <Content>
        {filteredItems.map((item) => (
          <MediaCard 
            key={item.path} 
            onClick={(e) => {
                e.stopPropagation();
                onClickMedia(item);
              }
            }
          >
            {item.type === MediaType.FOLDER ? (
              <IcFolder />
            ) : (
              <Zone>
                {item.type === MediaType.VIDEO ? (
                  <video src={item.url || undefined} className='media-video' muted playsInline />
                ) : item.type === MediaType.IMAGE ? (
                  <img src={item.url || undefined} className='media-image' alt={item.name} />
                ) : null}
              </Zone>
            )}

            <Text body_1 medium left ellipsis lineClamp={2} style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
              {item.name}
            </Text>
          </MediaCard>
        ))}
      </Content>
    </Container>
  );
};

export default MediaContent;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const MediaCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  width: 100%;
  min-width: 0;
`;

const Zone = styled.div`
  position: relative;
  height: 82.41px;
  background: ${Colors.neutral.text[3]};
  border-radius: 4px;
  

  .media-video,
  .media-image {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    border-radius: 4px;
  }
`;
